import React, { useEffect, useRef, useState } from 'react';
import {
  getMezzi, getCarte, createCarta, updateCarta, deleteCarta,
  exportCarteXlsx, importCarteXlsx,
  Mezzo, Carta,
} from '../../services/fleet';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const TIPOLOGIE = ['carta_carburante', 'telepass', 'viacard', 'tessera_parcheggio', 'altro'];
const STATI     = ['attiva', 'non_attiva', 'sospesa', 'scaduta'];

const TIPO_ICONS: Record<string, string> = {
  carta_carburante: '⛽', telepass: '🛣️', viacard: '💳',
  tessera_parcheggio: '🅿️', altro: '📌',
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  attiva:     { bg: '#dcfce7', color: '#15803d' },
  non_attiva: { bg: '#f3f4f6', color: '#6b7280' },
  sospesa:    { bg: '#fef9c3', color: '#854d0e' },
  scaduta:    { bg: '#fee2e2', color: '#991b1b' },
};

const EMPTY: Partial<Carta> = { tipologia: 'carta_carburante', status: 'attiva' };

export default function CarteFleet() {
  const [mezzi, setMezzi]               = useState<Mezzo[]>([]);
  const [carte, setCarte]               = useState<Carta[]>([]);
  const [loading, setLoading]           = useState(true);
  const [filterTipo, setFilterTipo]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm]         = useState(false);
  const [editing, setEditing]           = useState<Carta | null>(null);
  const [form, setForm]                 = useState<Partial<Carta>>(EMPTY);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [importing, setImporting]       = useState(false);
  const importRef                       = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      const params: Record<string, unknown> = {};
      if (filterTipo)   params.tipologia = filterTipo;
      if (filterStatus) params.status    = filterStatus;
      const res = await exportCarteXlsx(params);
      downloadBlob(res.data as Blob, 'fleet_carte.xlsx');
    } catch { setError('Errore export'); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importCarteXlsx(file);
      const { created, updated, errors } = res.data;
      alert(`Import completato: ${created} create, ${updated} aggiornate${errors.length ? '\nErrori: ' + errors.join('\n') : ''}`);
      load();
    } catch { setError('Errore import'); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
  };

  useEffect(() => {
    getMezzi(false).then(r => setMezzi(r.data));
    load();
  }, []);

  useEffect(() => { load(); }, [filterTipo, filterStatus]);

  const load = async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (filterTipo)   params.tipologia = filterTipo;
      if (filterStatus) params.status    = filterStatus;
      const res = await getCarte(params);
      setCarte(res.data);
    } catch {
      setError('Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  const openNew  = () => { setEditing(null);    setForm({ ...EMPTY }); setShowForm(true); setError(''); };
  const openEdit = (c: Carta) => { setEditing(c); setForm({ ...c }); setShowForm(true); setError(''); };

  const handleSave = async () => {
    if (!form.tipologia) { setError('Tipo obbligatorio'); return; }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await updateCarta(editing.id, form);
      } else {
        await createCarta(form);
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Eliminare questa carta?')) return;
    try { await deleteCarta(id); load(); } catch { setError('Errore eliminazione'); }
  };

  const set = (patch: Partial<Carta>) => setForm(f => ({ ...f, ...patch }));

  return (
    <div className="fleet-section">
      <div className="fleet-toolbar">
        <select className="fleet-filter" value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
          <option value="">Tutti i tipi</option>
          {TIPOLOGIE.map(t => <option key={t} value={t}>{TIPO_ICONS[t]} {t.replace(/_/g, ' ')}</option>)}
        </select>
        <select className="fleet-filter" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Tutti gli stati</option>
          {STATI.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <button className="btn-secondary" onClick={handleExport} title="Scarica XLSX">⬇ XLSX</button>
        <button className="btn-secondary" onClick={() => importRef.current?.click()} disabled={importing} title="Carica XLSX">
          {importing ? '...' : '⬆ XLSX'}
        </button>
        <input ref={importRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImport} />
        <button className="btn-primary" onClick={openNew}>+ Nuova Carta</button>
      </div>

      {error && <div className="fleet-error">{error}</div>}

      {loading ? (
        <div className="fleet-loading">Caricamento...</div>
      ) : (
        <table className="fleet-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Status</th>
              <th>Intestatario</th>
              <th>Veicolo</th>
              <th>Autista</th>
              <th>Codice</th>
              <th>Limite</th>
              <th>Scadenza</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {carte.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>Nessuna carta registrata</td></tr>
            ) : carte.map(c => {
              const sc = STATUS_COLORS[c.status] ?? STATUS_COLORS.non_attiva;
              return (
                <tr key={c.id}>
                  <td>{TIPO_ICONS[c.tipologia]} {c.tipologia.replace(/_/g, ' ')}</td>
                  <td>
                    <span style={{ background: sc.bg, color: sc.color, padding: '2px 8px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600 }}>
                      {c.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td>{c.intestatario || '—'}</td>
                  <td>{c.mezzo_targa  || '—'}</td>
                  <td>{c.autista_nome || '—'}</td>
                  <td><code style={{ fontSize: '0.8rem' }}>{c.codice || '—'}</code></td>
                  <td>{c.limite || '—'}</td>
                  <td>{c.data_scadenza ? new Date(c.data_scadenza).toLocaleDateString('it-IT') : '—'}</td>
                  <td>
                    <button className="btn-icon" onClick={() => openEdit(c)}>✏️</button>
                    <button className="btn-icon btn-danger" onClick={() => handleDelete(c.id)}>🗑️</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="fleet-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="fleet-modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
            <div className="fleet-modal-header">
              <h2>{editing ? 'Modifica Carta' : 'Nuova Carta / Telepass'}</h2>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="fleet-modal-body">
              {error && <div className="fleet-error">{error}</div>}
              <div className="form-grid-3">
                <div className="form-group">
                  <label>Tipo *</label>
                  <select value={form.tipologia || ''} onChange={e => set({ tipologia: e.target.value })}>
                    {TIPOLOGIE.map(t => <option key={t} value={t}>{TIPO_ICONS[t]} {t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status || 'attiva'} onChange={e => set({ status: e.target.value })}>
                    {STATI.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Intestatario</label>
                  <input value={form.intestatario || ''} onChange={e => set({ intestatario: e.target.value })} />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Veicolo associato</label>
                  <select value={form.mezzo_id || ''} onChange={e => set({ mezzo_id: parseInt(e.target.value) || undefined })}>
                    <option value="">— Nessun veicolo —</option>
                    {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Codice / Numero</label>
                  <input value={form.codice || ''} onChange={e => set({ codice: e.target.value })} />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Limite / Descrizione limite</label>
                  <input value={form.limite || ''} onChange={e => set({ limite: e.target.value })} placeholder="Es. €500/mese, solo gasolio..." />
                </div>
                <div className="form-group">
                  <label>Data emissione</label>
                  <input type="date" value={form.data_emissione || ''} onChange={e => set({ data_emissione: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Data scadenza</label>
                  <input type="date" value={form.data_scadenza || ''} onChange={e => set({ data_scadenza: e.target.value })} />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Note</label>
                  <textarea rows={2} value={form.note || ''} onChange={e => set({ note: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="fleet-modal-footer">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Annulla</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : (editing ? 'Aggiorna' : 'Salva')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
